/**
 * PDP accordion open/close animation for browsers without `::details-content`
 * (T-0256).
 *
 * The theme draws the panel through the pseudo-element: `accordion-custom`
 * (snippets/accordion-custom-component.liquid) transitions `block-size`,
 * `opacity` and `content-visibility` on `details::details-content`, and
 * sections/product-information.liquid re-times that to 400ms open / 350ms
 * close, with a `grid-template-rows` fallback for engines that have the
 * pseudo but not `interpolate-size` (Firefox today).
 *
 * None of it reaches a browser that does not implement `::details-content`.
 * Every one of those rules is written as a selector LIST that mixes the pseudo
 * with `.details-content`, and one invalid selector drops the whole list — so
 * there the panel has no transition declared at all, and none could be added:
 * a closed `<details>` does not render its content, so there is no box to
 * animate from. That is why the CSS-only attempt on this task changed nothing
 * in Safari, and why this file exists.
 *
 * It is deliberately inert wherever the CSS already works. The one capability
 * test is `::details-content` itself: if the engine has it, the stylesheet owns
 * the animation and this script returns before binding anything.
 *
 * Timings are read from the same custom properties the CSS uses, so the two
 * paths cannot drift.
 */
(() => {
  /* Rows this owns — the same scope the 400/350 pair is written against. */
  const DETAILS_SELECTOR = '.product-details accordion-custom details';
  /* Direct child only — a row may itself contain an accordion. */
  const CONTENT_SELECTOR = ':scope > .details-content';

  const cssOwnsTheAnimation = (() => {
    try {
      return window.CSS?.supports?.('selector(::details-content)') === true;
    } catch {
      return false;
    }
  })();
  if (cssOwnsTheAnimation) return;

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  /** Reads a CSS time value ("400ms", "0.4s") as a number of milliseconds. */
  const milliseconds = (value, fallback) => {
    const raw = String(value).trim();
    const number = parseFloat(raw);
    if (!Number.isFinite(number)) return fallback;
    if (raw.endsWith('ms')) return number;
    if (raw.endsWith('s')) return number * 1000;
    return fallback;
  };

  /* The fallbacks mirror the values in sections/product-information.liquid, for
     the case where the section stylesheet has not applied yet. */
  const timingFor = (details) => {
    const styles = getComputedStyle(details);
    return {
      open: milliseconds(styles.getPropertyValue('--animation-speed-slow'), 400),
      close: milliseconds(styles.getPropertyValue('--forcast-accordion-close-speed'), 350),
      easing: styles.getPropertyValue('--animation-easing').trim() || 'ease',
    };
  };

  /* One animation per panel: a second click has to take the first one's place
     rather than race it. */
  const running = new WeakMap();

  const animate = (content, from, to, duration, easing) => {
    const previous = running.get(content);
    if (previous) {
      running.delete(content);
      previous.cancel();
    }

    content.style.overflow = 'hidden';
    const animation = content.animate(
      [
        { blockSize: `${from}px`, opacity: from === 0 ? 0 : 1 },
        { blockSize: `${to}px`, opacity: to === 0 ? 0 : 1 },
      ],
      { duration, easing, fill: 'forwards' }
    );
    running.set(content, animation);

    /* `fill: forwards` holds the last frame, so a closing panel cannot flash
       back to its natural height in the gap between the animation ending and
       `open` being committed. Resolves with the function that drops that hold —
       or with null when a later click cancelled this one, in which case the
       caller must not commit anything. */
    return animation.finished.then(
      (settled) => {
        running.delete(content);
        return () => {
          settled.cancel();
          content.style.overflow = '';
        };
      },
      () => null
    );
  };

  const heightOf = (content) => content.getBoundingClientRect().height;

  const expand = (details, content, timing) => {
    details.open = true;
    animate(content, 0, heightOf(content), timing.open, timing.easing).then((settle) => settle?.());
  };

  const collapse = (details, content, timing) =>
    animate(content, heightOf(content), 0, timing.close, timing.easing).then((settle) => {
      if (!settle) return;
      details.open = false;
      settle();
    });

  /* Product rows share a `name`, so `<details>` is an exclusive group: the
     browser slams the open sibling shut the instant this row opens, leaving
     nothing to draw. Lifting `name` for the length of its close hands that job
     back to us — it goes straight back on, whether the close finished or was
     cancelled, so the group is never left without its exclusivity. */
  const closeGroupSiblings = (details, timing) => {
    const name = details.getAttribute('name');
    if (!name) return;

    for (const sibling of document.querySelectorAll(`details[name="${CSS.escape(name)}"][open]`)) {
      if (sibling === details) continue;

      const content = sibling.querySelector(CONTENT_SELECTOR);
      sibling.removeAttribute('name');
      const restore = () => sibling.setAttribute('name', name);

      if (!content || typeof content.animate !== 'function') {
        sibling.open = false;
        restore();
        continue;
      }

      collapse(sibling, content, timing).then(restore, restore);
    }
  };

  /* Delegated from `document`: the details rows are section-rendered and come
     back as new elements whenever the PDP re-renders for a variant change. */
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    /* AccordionCustom cancels the click on the breakpoints where a row is
       disabled (assets/accordion-custom.js); a cancelled toggle has nothing to
       draw. This listener is on `document`, so that call has already happened. */
    if (event.defaultPrevented) return;

    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (!summary || !(details instanceof HTMLDetailsElement)) return;
    if (!details.matches(DETAILS_SELECTOR)) return;

    const content = details.querySelector(CONTENT_SELECTOR);
    if (!content || typeof content.animate !== 'function') return;

    /* Reduced motion wants the native toggle, not a drawn one. */
    if (reduceMotion?.matches) return;

    event.preventDefault();

    try {
      const timing = timingFor(details);
      if (details.open) {
        collapse(details, content, timing);
      } else {
        closeGroupSiblings(details, timing);
        expand(details, content, timing);
      }
    } catch {
      /* A row that cannot be drawn must still open. */
      details.open = !details.open;
    }
  });
})();
